import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, Option } from '../../../../components/shared/nextgen/dropdown';
import SearchInput from '../../../../components/shared/search-input';
import { Lx } from '../../../../i18n/consts';
import MobileFilter from '../../../../components/shared/mobile-dialog';
import styles from './type-filter.module.scss';
import PageTableContainer from '../../../../components/shared/page-table-container';

interface FilterContentProps {
    search: string;
    selectedStatus: number;
    onSearchChange: (value: string) => void;
    onStatusChange: (value: number) => void;
    isMobile: boolean;
}

const FilterContent = ({
    search,
    selectedStatus,
    onSearchChange,
    onStatusChange,
    isMobile,
}: FilterContentProps) => {
    const intl = useIntl();

    const statusOptions = [
        { id: -1, name: intl.formatMessage({ id: Lx.General.ALL_STATUSES }) },
        { id: 0, name: intl.formatMessage({ id: Lx.General.ACTIVE }) },
        { id: 1, name: intl.formatMessage({ id: Lx.General.SUSPENDED }) },
    ];

    return (
        <div className={styles.typeFilterContainer}>
            <SearchInput
                searchValue={search}
                onChange={onSearchChange}
                width={isMobile ? '100%' : '260px'}
            />
            <Dropdown
                style={{
                    marginBottom: 0,
                    fontSize: '16px',
                    width: isMobile ? '100%' : '260px',
                }}
                selectedTextColor="secondary"
                name='statusFilter'
                value={selectedStatus}
                placeholder={intl.formatMessage({ id: Lx.General.ALL_STATUSES })}
                onChange={(_, value) => onStatusChange(value as number)}
            >
                {statusOptions.map(option => (
                    <Option key={option.id} value={option.id}>
                        {option.name}
                    </Option>
                ))}
            </Dropdown>
        </div>
    );
};

export const DEFAULT_TYPE_FILTER_STATE = {
    search: '',
    selectedStatus: -1,
};

interface TypeFilterProps {
    search: string;
    selectedStatus: number;
    isMobile: boolean;
    mobileView: 'list' | 'filter';
    onFilterChange: (payload: { search: string; selectedStatus: number }) => void;
    onMobileClose: () => void;
    children: React.ReactNode;
}

export const TypeFilter = ({
    search,
    selectedStatus,
    isMobile,
    mobileView,
    onFilterChange,
    onMobileClose,
    children,
}: TypeFilterProps) => {
    const [pendingSearch, setPendingSearch] = useState(search);
    const [pendingStatus, setPendingStatus] = useState<number>(selectedStatus);

    useEffect(() => {
        if (isMobile && mobileView === 'filter') {
            setPendingSearch(search);
            setPendingStatus(selectedStatus);
        }
    }, [isMobile, mobileView, search, selectedStatus]);

    useEffect(() => {
        if (!isMobile) {
            setPendingSearch(search);
            setPendingStatus(selectedStatus);
        }
    }, [isMobile, search, selectedStatus]);

    const handleDesktopSearchChange = (value: string) => {
        onFilterChange({ search: value, selectedStatus });
    };

    const handleDesktopStatusChange = (value: number) => {
        onFilterChange({ search, selectedStatus: value });
    };

    const handleMobileApply = () => {
        onFilterChange({ search: pendingSearch, selectedStatus: pendingStatus });
        onMobileClose();
    };

    const handleMobileReset = () => {
        setPendingSearch(DEFAULT_TYPE_FILTER_STATE.search);
        setPendingStatus(DEFAULT_TYPE_FILTER_STATE.selectedStatus);
        onFilterChange(DEFAULT_TYPE_FILTER_STATE);
    };

    if (isMobile && mobileView === 'filter') {
        return (
            <MobileFilter
                onCancel={handleMobileReset}
                onConfirm={handleMobileApply}
                cancelLocalizationId={Lx.General.RESET_FILTER}
            >
                <FilterContent
                    search={pendingSearch}
                    selectedStatus={pendingStatus}
                    onSearchChange={setPendingSearch}
                    onStatusChange={setPendingStatus}
                    isMobile
                />
            </MobileFilter>
        );
    }

    return (
        <div className={styles.layout}>
            {!isMobile ? (
                <div className={styles.desktopFilterWrapper}>
                    <FilterContent
                        search={search}
                        selectedStatus={selectedStatus}
                        onSearchChange={handleDesktopSearchChange}
                        onStatusChange={handleDesktopStatusChange}
                        isMobile={false}
                    />
                </div>
            ) : null}
            <div className={styles.content}>
                <PageTableContainer>
                    {children}
                </PageTableContainer>
            </div>
        </div>
    );
};

export default TypeFilter;
